const responses = new Map();
let activeChooser;

/** Only same-store product routes; never carry a selected size to another product. */
export function colourCardURL(value, base = window.location.href) {
  const url = new URL(value, base);
  const current = new URL(base);
  if (url.origin !== current.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:collections\/[^/]+\/)?products\/[^/]+\/?$/i.test(url.pathname)) {
    throw new Error('Invalid colour destination');
  }
  url.search = '';
  url.searchParams.set('view', 'colour-card');
  const preview = current.searchParams.get('preview_theme_id');
  if (preview) url.searchParams.set('preview_theme_id', preview);
  url.searchParams.set('pb', '0');
  return url;
}

/** Each card owns its requests, presentation and native quick-add children. */
class LazyOafCardColours extends HTMLElement {
  #events;
  #request;
  #version = 0;
  #card;
  #panel;
  #trigger;
  #groupLoaded = false;
  #applying = false;
  #retry;
  #disabled = new Map();
  #geometry;
  connectedCallback() {
    if (this.#applying) return;
    this.#events?.abort();
    this.#events = new AbortController();
    queueMicrotask(() => {
      if (!this.isConnected || this.#events.signal.aborted) return;
      this.#card = this.closest('product-card');
      this.#panel = this.querySelector('.lazy-oaf-card-colours__panel');
      this.#trigger = this.#card?.querySelector('[data-card-colour-trigger]');
      if (!this.#card || !this.#panel || !this.#trigger) return;
      // The server link remains useful when JavaScript is absent.
      if (this.#trigger.tagName === 'A') {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = this.#trigger.className;
        button.dataset.cardColourTrigger = this.#trigger.getAttribute('href');
        button.textContent = this.#trigger.textContent;
        this.#trigger.replaceWith(button);
        this.#trigger = button;
      }
      const panelId = `card-colours-${crypto.randomUUID()}`;
      this.#panel.id = panelId;
      this.#trigger.setAttribute('aria-controls', panelId);
      this.#trigger.setAttribute('aria-expanded', 'false');
      const signal = this.#events.signal;
      this.#card.addEventListener('click', event => {
        if (event.target.closest('[data-card-colour-trigger]') !== this.#trigger) return;
        event.preventDefault(); event.stopPropagation();
        if (this.#panel.hidden) this.#open(event.detail !== 0); else this.#close();
      }, { signal });
      this.addEventListener('click', event => {
        event.stopPropagation();
        if (event.target.closest('.lazy-oaf-card-colours__close')) this.#close();
        if (event.target.closest('.lazy-oaf-card-colours__retry')) this.#retry?.();
      }, { signal });
      this.addEventListener('pointerdown', event => event.stopPropagation(), { signal });
      this.addEventListener('keydown', event => {
        event.stopPropagation();
        if (event.key === 'Escape') { event.preventDefault(); this.#close(); }
      }, { signal });
      this.addEventListener('volt:image-option-select', event => {
        event.preventDefault(); event.stopPropagation();
        this.#choose(event.detail);
      }, { signal });
      document.addEventListener('pointerdown', event => {
        if (!this.#panel.hidden && !this.contains(event.target) && !this.#trigger.contains(event.target)) this.#close(false);
      }, { signal });
    });
  }
  async #open(pointer) {
    if (activeChooser && activeChooser !== this) activeChooser.#close(false);
    activeChooser = this;
    // A disclosure in a carousel's peeking card must reveal the whole chooser.
    this.#card.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    this.#panel.hidden = false;
    this.#panel.toggleAttribute('data-pointer-open', pointer);
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#observeGeometry();
    if (this.#groupLoaded) return;
    this.#retry = () => this.#open(pointer);
    this.#status(this.dataset.loadingLabel);
    try {
      const html = await this.#load(this.#trigger.dataset.cardColourTrigger);
      if (!html) return;
      const group = html.querySelector('template[data-card-colour-options]')?.content.cloneNode(true);
      if (!group?.querySelector('volt-image-options')) throw new Error('No colour choices');
      this.querySelector('[data-colour-options]').replaceChildren(group);
      const count = this.querySelector('.lazy-oaf-product-colours')?.dataset?.colourCountLabel;
      if (count) this.#trigger.textContent = count;
      this.#groupLoaded = true;
      this.#status('');
    } catch (error) { this.#failure(error); }
  }
  #close(returnFocus = true) {
    if (activeChooser === this) activeChooser = undefined;
    const inside = this.contains(document.activeElement);
    this.#cancel();
    this.#panel.hidden = true;
    this.#geometry?.disconnect();
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#status('');
    if (returnFocus || inside) this.#trigger.focus({ preventScroll: true });
  }
  async #choose(choice) {
    this.#retry = () => this.#choose(choice);
    this.#status(this.dataset.updatingLabel);
    this.#setBusy(true);
    this.querySelector('volt-image-options')?.pending(choice.url, choice.activation !== 'keyboard');
    try {
      const html = await this.#load(choice.url);
      if (!html) return;
      this.#apply(html);
      this.querySelector('volt-image-options')?.select(choice.url);
      this.#observeGeometry();
      this.#status('');
      this.#setBusy(false);
    } catch (error) { this.#failure(error); }
  }
  async #load(url) {
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    const version = ++this.#version;
    const key = colourCardURL(url).href;
    let source = responses.get(key);
    if (!source) {
      try {
        const response = await fetch(key, { signal: request.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Colour response failed');
        source = await response.text();
      } catch (error) {
        if (request.signal.aborted || version !== this.#version) return;
        throw error;
      }
      if (request.signal.aborted || version !== this.#version || !this.isConnected || this.#panel.hidden) return;
      responses.set(key, source);
      if (responses.size > 24) responses.delete(responses.keys().next().value);
    }
    if (request.signal.aborted || version !== this.#version || !this.isConnected || this.#panel.hidden) return;
    return new DOMParser().parseFromString(source, 'text/html');
  }
  #apply(html) {
    const gallery = this.closest('.card-gallery');
    const profile = gallery.dataset.colourCardProfile || 'rail';
    const template = html.querySelector(`template[data-card-colour-fragment="${profile}"]`);
    if (!template) throw new Error('Missing selected card');
    // Unique form and media IDs even if several cards choose the same colour.
    const markup = template.innerHTML.replaceAll('ColourCardResponse', `ColourCard-${crypto.randomUUID()}`);
    const selected = new DOMParser().parseFromString(markup, 'text/html').querySelector('product-card');
    const selectedGallery = selected?.querySelector('.card-gallery');
    const selectedPrice = selected?.querySelector('product-price [ref="priceContainer"]');
    const selectedLink = selected?.querySelector('[ref="productCardLink"]');
    const title = selected?.querySelector('.lazy-oaf-product-card__title');
    if (!selectedGallery || !selectedPrice || !selectedLink || !title) throw new Error('Incomplete selected card');
    const card = this.#card;
    const focused = this.contains(document.activeElement) ? document.activeElement : null;
    if (gallery.dataset.colourCardQuickAdd !== 'true') selectedGallery.querySelector('quick-add-component')?.remove();
    // Preserve host badge settings; the server's shared badge renderer owns sale/stock priority.
    const prefix = gallery.dataset.colourCardBadgePrefix || '';
    const tags = JSON.parse(html.querySelector('[data-colour-product-tags]')?.dataset.colourProductTags || '[]');
    const derivedTag = prefix ? tags.find(tag => tag.startsWith(prefix) && tag.slice(prefix.length).trim()) : null;
    const label = gallery.dataset.colourCardBadgeLabel || (derivedTag ? derivedTag.slice(prefix.length).trim() : '');
    const kind = gallery.dataset.colourCardBadgeLabel ? 'explicit' : label ? 'derived' : 'automatic';
    const percentage = gallery.dataset.colourCardBadgePercentage === 'true' ? 1 : 0;
    const badgeTemplate = html.querySelector(`template[data-colour-badge="${kind}-${percentage}"]`);
    if (!badgeTemplate) throw new Error('Missing selected badges');
    selectedGallery.querySelector('.product-badges')?.remove();
    const badges = badgeTemplate.content.cloneNode(true);
    const badge = badges.querySelector('.product-badges');
    if (badge) {
      badge.classList.toggle('product-badges--editorial', gallery.dataset.colourCardBadgePresentation === 'editorial');
      const text = badge.querySelector('.product-badges__badge');
      text.textContent = text.textContent.replace('ColourCardLabelPlaceholder', label).replace('ColourCardSaleSuffix', gallery.dataset.colourCardBadgeSuffix || 'OFF');
      selectedGallery.append(badge);
    }
    const currentPrices = [...card.querySelectorAll('product-price [ref="priceContainer"]')];
    if (!currentPrices.length) throw new Error('Missing card price');
    // Preserve merchant layout/styles and the active chooser; replace native commerce/media as one commit.
    this.#applying = true;
    try {
      // Keep the chooser connected: removing it resets inner scroll and keyboard focus.
      for (const child of [...gallery.childNodes]) if (child !== this) child.remove();
      gallery.prepend(...selectedGallery.childNodes);
      gallery.dataset.productId = selected.dataset.productId;
      for (const attribute of ['on:pointerenter', 'on:pointerleave']) {
        if (selectedGallery.hasAttribute(attribute)) gallery.setAttribute(attribute, selectedGallery.getAttribute(attribute));
        else gallery.removeAttribute(attribute);
      }
      card.dataset.productId = selected.dataset.productId;
      const featuredMedia = selected.getAttribute('data-featured-media-url');
      if (featuredMedia) card.setAttribute('data-featured-media-url', featuredMedia);
      else card.removeAttribute('data-featured-media-url');
      const oldPayload = card.getAttribute('view-event-payload');
      const newPayload = selected.getAttribute('view-event-payload');
      if (newPayload) {
        try {
          const next = JSON.parse(newPayload); const previous = JSON.parse(oldPayload || '{}');
          if ('context' in previous) next.context = previous.context;
          card.setAttribute('view-event-payload', JSON.stringify(next));
        } catch { card.setAttribute('view-event-payload', newPayload); }
      }
      for (const ref of ['productCardLink', 'cardGalleryLink', 'productTitleLink']) {
        for (const link of card.querySelectorAll(`[ref="${ref}"]`)) {
          link.setAttribute('href', selectedLink.getAttribute('href'));
          link.setAttribute('aria-label', title.textContent);
        }
      }
      for (const node of card.querySelectorAll('.lazy-oaf-product-card__title, [ref="productTitleLink"] p, .card-gallery__title .title-text')) node.textContent = title.textContent;
      for (const price of currentPrices) {
        price.replaceWith(selectedPrice.cloneNode(true));
      }
      for (const price of card.querySelectorAll('product-price')) price.dataset.productId = selected.dataset.productId;
      const content = card.querySelector('.product-card__content');
      const nextContent = selected.querySelector('.product-card__content');
      for (const property of ['--quick-add-display', '--quick-add-mobile-display', '--quick-add-mobile-opacity']) {
        content.style.setProperty(property, gallery.dataset.colourCardQuickAdd === 'true' ? nextContent.style.getPropertyValue(property) : (property.endsWith('opacity') ? '0' : 'none'));
      }
      // Keep the source group's non-reciprocal relationships and count while comparing colours.
      this.#trigger.dataset.cardColourTrigger = selectedLink.getAttribute('href');
    } finally {
      this.#applying = false;
      if (focused?.isConnected) focused.focus({ preventScroll: true });
    }
  }
  #observeGeometry() {
    this.#geometry?.disconnect();
    const gallery = this.closest('.card-gallery');
    const measure = () => {
      const bounds = gallery.getBoundingClientRect();
      const buttons = [...gallery.querySelectorAll('.quick-add__button')];
      const top = buttons.reduce((edge, button) => {
        const rect = button.getBoundingClientRect();
        return rect.width && rect.height ? Math.min(edge, rect.top) : edge;
      }, bounds.bottom);
      this.#panel.style.setProperty('--card-colours-cta-space', `${Math.max(0, bounds.bottom - top)}px`);
    };
    this.#geometry = new ResizeObserver(measure);
    this.#geometry.observe(gallery);
    for (const button of gallery.querySelectorAll('.quick-add__button')) this.#geometry.observe(button);
    measure();
  }
  #setBusy(busy) {
    this.#panel?.setAttribute('aria-busy', String(busy));
    if (busy) {
      for (const button of this.#card.querySelectorAll('quick-add-component button')) {
        if (!this.#disabled.has(button)) this.#disabled.set(button, button.disabled);
        button.disabled = true;
      }
    } else {
      this.querySelector('volt-image-options')?.pending();
      for (const [button, disabled] of this.#disabled) if (button.isConnected) button.disabled = disabled;
      this.#disabled.clear();
    }
  }
  #status(message, failed = false) {
    this.querySelector('.lazy-oaf-card-colours__status').textContent = message;
    this.#panel.toggleAttribute('data-error', failed);
    this.querySelector('.lazy-oaf-card-colours__retry').hidden = !failed;
  }
  #failure(error) {
    if (error.name === 'AbortError' || !this.isConnected || this.#panel.hidden) return;
    this.#setBusy(false);
    this.#status(this.dataset.errorLabel, true);
  }
  #cancel() { this.querySelector('volt-image-options')?.cancelHover(); this.#version++; this.#request?.abort(); this.#setBusy(false); }
  disconnectedCallback() {
    if (this.#applying) return;
    if (activeChooser === this) activeChooser = undefined;
    this.#cancel();
    this.#geometry?.disconnect();
    this.#events?.abort();
  }
}
if (!customElements.get('lazy-oaf-card-colours')) customElements.define('lazy-oaf-card-colours', LazyOafCardColours);
